import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Upload, Sparkles, Loader2, ArrowRight, RotateCcw, Phone } from 'lucide-react';
import { Nav } from '../components/sections/Nav';
import { Footer } from '../components/sections/Footer';
import { useLenis } from '../lib/useLenis';

const API = process.env.REACT_APP_BACKEND_URL;
const PHONE_HREF = 'tel:+15055893461';

const AREAS = ['Interior Room', 'Exterior', 'Cabinets', 'Commercial'];
const STYLES = ['Modern', 'Warm & Cozy', 'Classic', 'Bright & Airy', 'Bold', 'Farmhouse'];
const SMS_TEXT = "I agree to receive text messages from MSD Painting Services about my paint preview and estimate. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help.";
const DISCLAIMER = "This is an AI visualization, not an exact representation of final paint colors. Colors look different on screens and in different light; we'll bring real samples to your free estimate.";

const BeforeAfter = ({ before, after }) => {
    const [pos, setPos] = useState(50);
    return (
        <div className="relative w-full overflow-hidden rounded-2xl border border-white/10 select-none" data-testid="paintvision-slider">
            <img src={after} alt="AI paint preview" className="block w-full h-auto" />
            <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
                <img src={before} alt="Original room" className="block h-full w-auto max-w-none" style={{ width: `${100 / (pos / 100)}%`, maxWidth: 'none' }} />
            </div>
            <div className="absolute top-0 bottom-0" style={{ left: `calc(${pos}% - 1px)` }}>
                <div className="w-0.5 h-full bg-adobe-500" />
            </div>
            <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-ink-900/70 backdrop-blur-sm font-mono text-[10px] tracking-[0.2em] uppercase text-bone-200">Before</div>
            <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-ink-900/70 backdrop-blur-sm font-mono text-[10px] tracking-[0.2em] uppercase text-adobe-500">After</div>
            <input
                type="range" min="0" max="100" value={pos}
                onChange={(e) => setPos(Number(e.target.value))}
                data-testid="paintvision-slider-range"
                aria-label="Drag to compare before and after"
                className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
            />
        </div>
    );
};

const PaintVision = () => {
    useLenis();
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [area, setArea] = useState('Interior Room');
    const [colors, setColors] = useState('');
    const [style, setStyle] = useState('');
    const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
    const [consent, setConsent] = useState(false);
    const [company, setCompany] = useState(''); // honeypot
    const [phase, setPhase] = useState('form'); // form | loading | result | failed
    const [result, setResult] = useState(null); // {original_url, preview_url}
    const [error, setError] = useState('');
    const [runCount, setRunCount] = useState(0);
    const pollRef = useRef(null);

    useEffect(() => {
        const prev = document.title;
        document.title = 'PaintVision — See Your Room in New Colors | MSD Painting Services';
        return () => { document.title = prev; };
    }, []);
    useEffect(() => () => clearInterval(pollRef.current), []);

    const onFile = (f) => {
        if (!f) return;
        if (!['image/jpeg', 'image/png'].includes(f.type)) { setError('Please upload a JPG or PNG image.'); return; }
        if (f.size > 10 * 1024 * 1024) { setError('That image is larger than 10 MB. Please choose a smaller photo.'); return; }
        setError('');
        setFile(f);
        setPreview(URL.createObjectURL(f));
    };

    const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
    const canSubmit = file && form.first_name.trim() && form.last_name.trim() && form.email.trim() && form.phone.trim();

    const poll = (jobId) => {
        const started = Date.now();
        pollRef.current = setInterval(async () => {
            if (Date.now() - started > 180000) { // 3 min cap
                clearInterval(pollRef.current);
                setPhase('failed');
                return;
            }
            try {
                const r = await fetch(`${API}/api/paintvision/jobs/${jobId}`);
                const data = await r.json();
                if (data.status === 'done' && data.preview_url) {
                    clearInterval(pollRef.current);
                    setResult({ original_url: `${API}${data.original_url}`, preview_url: `${API}${data.preview_url}` });
                    setPhase('result');
                } else if (data.status === 'failed') {
                    clearInterval(pollRef.current);
                    setPhase('failed');
                }
            } catch { /* keep polling */ }
        }, 3000);
    };

    const submit = async (e) => {
        e && e.preventDefault();
        if (!canSubmit || phase === 'loading') return;
        setError('');
        setPhase('loading');
        const fd = new FormData();
        fd.append('photo', file);
        fd.append('area', area);
        fd.append('colors', colors);
        fd.append('style', style);
        fd.append('first_name', form.first_name);
        fd.append('last_name', form.last_name);
        fd.append('email', form.email);
        fd.append('phone', form.phone);
        fd.append('sms_consent', consent ? 'true' : 'false');
        fd.append('company', company);
        try {
            const r = await fetch(`${API}/api/paintvision/jobs`, { method: 'POST', body: fd });
            if (r.status === 429) {
                const d = await r.json().catch(() => ({}));
                setError(d.detail || "You've reached the preview limit for now. Please call (505) 589-3461 or book your free estimate.");
                setPhase('form');
                return;
            }
            if (!r.ok) { setPhase('failed'); return; }
            const d = await r.json();
            setRunCount((c) => c + 1);
            poll(d.job_id);
        } catch {
            setPhase('failed');
        }
    };

    const tryDifferent = () => {
        setPhase('form');
        setResult(null);
        // keep the same file; user can tweak colors/style and re-run
    };

    return (
        <div className="relative min-h-screen bg-ink-900 text-bone-100" data-testid="paintvision-page">
            <Nav />
            <section className="relative overflow-hidden border-b border-white/10">
                <div aria-hidden className="pointer-events-none absolute inset-0 opacity-30" style={{ background: 'radial-gradient(ellipse at 20% 30%, rgba(116,135,154,0.20) 0%, rgba(116,135,154,0) 55%), radial-gradient(ellipse at 85% 70%, rgba(185,175,161,0.16) 0%, rgba(185,175,161,0) 55%)' }} />
                <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 pt-32 md:pt-40 pb-12 md:pb-16">
                    <div className="mb-6 flex items-center gap-4">
                        <span className="w-10 h-px bg-adobe-500" />
                        <span className="eyebrow text-bone-200">PaintVision · Free AI Preview</span>
                    </div>
                    <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                        className="font-display font-black uppercase tracking-[-0.03em] leading-[0.92] text-bone-100 text-[12vw] md:text-[7vw] lg:text-[5.4rem]">
                        See your room in <span className="italic font-light text-adobe-500">new colors.</span>
                    </motion.h1>
                    <p className="mt-6 max-w-2xl font-body text-lg font-light text-bone-200 leading-relaxed">
                        Upload a photo and our AI shows you a realistic preview of your space, freshly painted. It takes under a minute — then we bring real samples to your free estimate.
                    </p>
                </div>
            </section>

            <section className="relative px-6 md:px-10 py-14 md:py-20">
                <div className="mx-auto max-w-[760px]">
                    {phase === 'form' && (
                        <form onSubmit={submit} className="space-y-6" data-testid="paintvision-form" noValidate>
                            {/* honeypot */}
                            <input type="text" value={company} onChange={(e) => setCompany(e.target.value)} name="company" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

                            {/* upload */}
                            <div>
                                <label className="block text-[11px] font-bold uppercase tracking-[0.1em] text-adobe-500 mb-2">Photo</label>
                                <label data-testid="paintvision-upload" className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-white/20 bg-white/[0.03] px-6 py-10 cursor-pointer hover:border-adobe-500/50 transition-colors text-center">
                                    {preview ? (
                                        <img src={preview} alt="Your upload" className="max-h-56 rounded-lg" />
                                    ) : (
                                        <><Upload size={22} className="text-adobe-500" /><span className="text-bone-200 text-sm">Click to upload a JPG or PNG (max 10 MB)</span></>
                                    )}
                                    <input type="file" accept="image/jpeg,image/png" className="hidden" data-testid="paintvision-file-input" onChange={(e) => onFile(e.target.files[0])} />
                                </label>
                                <p className="mt-2 text-[12px] text-bone-500">Upload one photo of the room or home. Daylight works best; please leave out people and personal items.</p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Select label="Area" value={area} onChange={setArea} options={AREAS} testid="paintvision-area" />
                                <Select label="Style (optional)" value={style} onChange={setStyle} options={STYLES} placeholder="Any style" testid="paintvision-style" />
                            </div>

                            <Field label="Colors or vibe (optional)" value={colors} onChange={setColors} placeholder="e.g. warm white walls with navy trim, or 'modern farmhouse'" testid="paintvision-colors" />

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field label="First name" value={form.first_name} onChange={(v) => setField('first_name', v)} required testid="paintvision-first" />
                                <Field label="Last name" value={form.last_name} onChange={(v) => setField('last_name', v)} required testid="paintvision-last" />
                                <Field label="Email" type="email" value={form.email} onChange={(v) => setField('email', v)} required testid="paintvision-email" />
                                <Field label="Mobile phone" type="tel" value={form.phone} onChange={(v) => setField('phone', v)} required testid="paintvision-phone" />
                            </div>

                            <label className="flex items-start gap-3 cursor-pointer">
                                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="paintvision-sms-consent" className="mt-1 w-4 h-4 shrink-0 accent-adobe-500" />
                                <span className="text-[12px] leading-[1.6] text-bone-300">
                                    {SMS_TEXT}{' '}
                                    <Link to="/privacy" className="text-adobe-500 underline">Privacy Policy</Link> and{' '}
                                    <Link to="/terms" className="text-adobe-500 underline">Terms</Link>.
                                </span>
                            </label>

                            {error && <p className="text-[13px] text-red-300" data-testid="paintvision-error">{error}</p>}

                            <button type="submit" disabled={!canSubmit} data-testid="paintvision-submit"
                                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 rounded-full bg-adobe-500 text-ink-900 font-semibold text-[15px] tracking-wide transition-transform hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0">
                                <Sparkles size={16} /> See my room in new colors
                            </button>
                        </form>
                    )}

                    {phase === 'loading' && (
                        <div className="py-20 text-center" data-testid="paintvision-loading">
                            <Loader2 size={34} className="mx-auto animate-spin text-adobe-500" />
                            <p className="mt-6 font-display text-2xl text-bone-100">Painting your room…</p>
                            <p className="mt-2 text-sm text-bone-400">This takes about 20–40 seconds.</p>
                        </div>
                    )}

                    {phase === 'result' && result && (
                        <div data-testid="paintvision-result">
                            <BeforeAfter before={result.original_url} after={result.preview_url} />
                            <p className="mt-4 text-[12px] leading-[1.6] text-bone-400" data-testid="paintvision-disclaimer">{DISCLAIMER}</p>
                            <div className="mt-7 flex flex-col sm:flex-row gap-4">
                                <Link to="/estimate" data-testid="paintvision-book" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-adobe-500 text-ink-900 font-semibold text-[14px] hover:-translate-y-0.5 transition-transform">
                                    Book my free estimate <ArrowRight size={16} />
                                </Link>
                                {runCount < 3 ? (
                                    <button onClick={tryDifferent} data-testid="paintvision-try-again" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-white/20 text-bone-100 font-semibold text-[14px] hover:border-adobe-500/60 transition-colors">
                                        <RotateCcw size={15} /> Try different colors
                                    </button>
                                ) : (
                                    <span className="inline-flex items-center text-[13px] text-bone-500 px-2">Preview limit reached for this photo.</span>
                                )}
                            </div>
                        </div>
                    )}

                    {phase === 'failed' && (
                        <div className="py-16 text-center" data-testid="paintvision-failed">
                            <p className="font-display text-2xl text-bone-100 max-w-lg mx-auto leading-snug">
                                We couldn't create your preview automatically, but our team has your photo and will send you color ideas personally.
                            </p>
                            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                                <Link to="/estimate" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-adobe-500 text-ink-900 font-semibold text-[14px]">Book my free estimate <ArrowRight size={16} /></Link>
                                <a href={PHONE_HREF} className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-white/20 text-bone-100 font-semibold text-[14px]"><Phone size={15} /> Call (505) 589-3461</a>
                            </div>
                        </div>
                    )}
                </div>
            </section>
            <Footer />
        </div>
    );
};

const Field = ({ label, value, onChange, placeholder, type = 'text', required, testid }) => (
    <div>
        <label className="block text-[11px] font-bold uppercase tracking-[0.1em] text-adobe-500 mb-2">{label}</label>
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} data-testid={testid}
            className="w-full px-4 py-3 rounded-xl border border-white/15 bg-white/[0.04] text-bone-100 text-[14.5px] placeholder:text-bone-500 focus:border-adobe-500 focus:outline-none focus:ring-2 focus:ring-adobe-500/20 transition" />
    </div>
);

const Select = ({ label, value, onChange, options, placeholder, testid }) => (
    <div>
        <label className="block text-[11px] font-bold uppercase tracking-[0.1em] text-adobe-500 mb-2">{label}</label>
        <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="w-full px-4 py-3 rounded-xl border border-white/15 bg-white/[0.04] text-bone-100 text-[14.5px] focus:border-adobe-500 focus:outline-none focus:ring-2 focus:ring-adobe-500/20 transition appearance-none">
            {placeholder && <option value="" className="bg-ink-900">{placeholder}</option>}
            {options.map((o) => <option key={o} value={o} className="bg-ink-900">{o}</option>)}
        </select>
    </div>
);

export default PaintVision;
